import { startLoadingState, endLoadingState, loadingTemplate } from "../basket-v2/basketLoading";
import { reportError } from "../basket-v2/errorReporting";
import dayJs from "dayjs";
import { ApiResponseWithData, BasketResponse, BasketItemRequest } from "./api/models";
import { getBasketUrl } from "./getBasketUrl";
import { setBasketExpiryTimer } from "./setBasketExpiryTimer";
import { showBasketNotificationBar } from "../alerts";
import { updateBasketBar } from "../basket-bar/updateBasketBar";
import { isBasketPage } from "../common/isBasketPage";
import { checkBasketExists } from "../common/checkBasketExists";
import { showConflictingBasketPopup } from "../alerts/showConflictingBasketPopup";
import { isSameBasketEvent } from "./isSameBasketEvent";
import { tryAbandonBasket } from "../common/tryAbandonBasket";
import { showTicketBasketFailure } from "../alerts/showTicketBasketFailureError";
import { attribute, postJson, trackEcommerceEvent } from "../../../utils";
import { attributes, ecommerceEvents } from "../../../scripts/constants";
import { updateDropdownsFromServer } from "./updateDropdownsFromServer";
import { constants } from "../common";

const addingTicketsMessage = loadingTemplate("Adding tickets to basket");

export async function addToBasketV2(target: HTMLElement, requests: BasketItemRequest[]) {
  startLoadingState(addingTicketsMessage, ".basket-overlay");

  const basketType = await checkBasketExists();
  if (requests.length == 0) return;

  var basketItemRequest: BasketItemRequest = requests[0];

  if (basketType === "v2" && (await isSameBasketEvent(basketItemRequest.cmsContentId)) !== true) {
    const shouldContinue = await showConflictingBasketPopup();
    if (!shouldContinue) {
      endLoadingState(".basket-overlay");
      return;
    }

    if ((await tryAbandonBasket(basketType)) === false) {
      reportError(
        "ft",
        "An error occurred while trying to abandon your existing basket. Please try cancelling it manually on the basket screen"
      );
      endLoadingState(".basket-overlay");
      return;
    }
  }

  const basketPath = await getBasketUrl();

  let response: ApiResponseWithData<BasketResponse>;
  try {
    response = await postJson<ApiResponseWithData<BasketResponse>>(basketPath, requests);
  } catch (e) {
    showTicketBasketFailure();
    endLoadingState(".basket-overlay");
    return;
  }

  if (!response) {
    showTicketBasketFailure();
    endLoadingState(".basket-overlay");
    return;
  }

  const { data, success, errors } = response;
  if (!success) {
    // Only report an error when the basket hasn't expired
    if (errors.every((error) => error.code !== 203)) {
      showTicketBasketFailure(errors[0].message);
    }

    endLoadingState(".basket-overlay");
    return;
  }

  var trackingData = {
    ...JSON.parse(attribute(target, attributes.itemTracking)),
    ...{ quantity: basketItemRequest.quantity },
  };

  trackEcommerceEvent({
    event: ecommerceEvents.add_to_cart,
    ecommerce: {
      value: trackingData.price * basketItemRequest.quantity,
      items: [trackingData],
    },
  });

  const { total, totalItems, expiryTime, checkoutUrl, basketUrl } = data;
  const url = isBasketPage() ? checkoutUrl : basketUrl;

  updateBasketBar({ totalItems, total, checkoutUrl: url });
  setBasketExpiryTimer(dayJs(expiryTime), url);
  updateDropdownsFromServer();
  window.localStorage.removeItem(constants.defaults.basketAlertLocalStorageKey);
  endLoadingState(".basket-overlay");

  if (!target.hasAttribute("data-ignore-basket-notification")) {
    await showBasketNotificationBar({
      products: [
        {
          eventId: basketItemRequest.eventId,
          productId: basketItemRequest.productId,
          zoneId: basketItemRequest.zoneId,
        },
      ],
      basketUrl: basketUrl,
    });
  }
}
